import { useEffect, useMemo, useState } from 'react';
import { Users, MessageSquare, TrendingUp, Mic, AlertCircle, Calendar, ChevronDown } from 'lucide-react';
import type { DateRange } from 'react-day-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { DateRangePicker } from './ui/date-range-picker';
import { PieChart, Pie, Cell, ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import TopModelsUsed from './TopModelsUsed';
import TopProjects from './TopProjects';
import TokenSpendOverTime, { MAX_RANGE_DAYS } from './TokenSpendOverTime';
import NeedsAttentionCard from './NeedsAttentionCard';
import PeopleAndAgentsPreview from './PeopleAndAgentsPreview';
import AgentsCostPanel from './AgentsCostPanel';
import { MemberDashboardCard } from './MemberDashboardCard';
import { ManagerBudgetStatusCard } from './ManagerBudgetStatusCard';
import { supabase } from '../lib/supabase';
import { useAuth, useTeamAccess } from '../contexts/AuthContext';

interface NewDashboardProps {
  teamId: string;
  teamName?: string;
  standupData?: any[];
  teamStats?: any;
  teamMemberCount?: number;
}

const NewDashboard = ({ teamId, teamName, standupData = [], teamStats, teamMemberCount = 1 }: NewDashboardProps) => {
  // 2026-09-14: this used to be a single `selectedDate` string driving a
  // <DatePicker>. The team flagged that TokenSpendOverTime's own separate
  // start/end range picker (added 2026-09-13) put TWO independent date
  // controls on one dashboard page with no clear hierarchy between them.
  // Consolidated to one control here: a <DateRangePicker> owns the real
  // state (`dateRange`), and every other widget below — which only ever
  // understood a single day — keeps working unchanged by reading a
  // `selectedDate` string derived from `dateRange.to` (see below). Picking
  // "Today" or "Yesterday" from the picker's presets collapses `from`/`to`
  // to the same day, so those widgets see exactly the single date they
  // always did.
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return { from: today, to: today };
  });

  // Pod filter — replaces a 2026-09-xx "team member" filter that was dead
  // UI: it computed a filtered list via a user_name match but
  // nothing downstream ever consumed it, since every KPI widget fetches its
  // own data by teamId/range rather than taking standupData as a prop. Pod
  // was chosen over per-member for two reasons: (1) it reuses an
  // authorization boundary that already exists (pod-manager permissions,
  // see 20260928201832_add_pod_managers_table.sql) rather than inventing a
  // new one, and (2) get_team_agent_usage_daily (the RPC every usage-based
  // widget reads from) already masks agent_id to NULL for anyone who isn't
  // an admin or that agent's pod manager — so a per-agent/per-pod filter is
  // only ever meaningful for those two roles anyway. Plain members keep
  // seeing the unfiltered, anonymized view they always have; the dropdown
  // itself is hidden for them below rather than offered and silently doing
  // nothing.
  const { role } = useTeamAccess(teamId);
  const { user } = useAuth();
  const canFilterByPod = role === 'owner' || role === 'admin' || role === 'manager';
  const canViewUsageDetails = canFilterByPod;

  const [selectedPodId, setSelectedPodId] = useState<string>('all');
  const [pods, setPods] = useState<{ id: string; name: string }[]>([]);
  // pod_id -> user ids in that pod, and user_id -> agent ids owned by that
  // user — both scoped to this team, loaded once per team/role so selecting
  // a pod is just a lookup, not a fresh round-trip.
  const [usersByPod, setUsersByPod] = useState<Map<string, string[]>>(new Map());
  const [agentsByUser, setAgentsByUser] = useState<Map<string, string[]>>(new Map());

  useEffect(() => {
    if (!canFilterByPod) {
      setPods([]);
      setUsersByPod(new Map());
      setAgentsByUser(new Map());
      return;
    }

    let cancelled = false;

    const load = async () => {
      const [podsResult, managedPodsResult, usersResult, agentsResult] = await Promise.all([
        supabase.from('pods').select('id, name').eq('team_id', teamId),
        role === 'manager' && user
          ? supabase.from('pod_managers').select('pod_id').eq('auth_user_id', user.id)
          : Promise.resolve({ data: null, error: null }),
        supabase.from('users').select('id, pod_id').eq('team_id', teamId),
        supabase.from('agents_team_visible').select('id, user_id').eq('team_id', teamId),
      ]);

      if (cancelled) return;

      if (podsResult.error || managedPodsResult.error || usersResult.error || agentsResult.error) {
        console.error(
          'Failed to load pod filter data:',
          podsResult.error ?? managedPodsResult.error ?? usersResult.error ?? agentsResult.error
        );
        return;
      }

      const allPods = (podsResult.data ?? []) as { id: string; name: string }[];
      // A manager only ever sees the pod(s) they're assigned to manage —
      // offering the rest would just produce an empty, confusing result for
      // them once selected (RLS already hides their detail). Admins see
      // every pod.
      const visiblePods =
        role === 'manager'
          ? allPods.filter((pod) =>
              ((managedPodsResult.data ?? []) as { pod_id: string }[]).some((m) => m.pod_id === pod.id)
            )
          : allPods;

      const nextUsersByPod = new Map<string, string[]>();
      for (const u of (usersResult.data ?? []) as { id: string; pod_id: string | null }[]) {
        if (!u.pod_id) continue;
        const list = nextUsersByPod.get(u.pod_id);
        if (list) list.push(u.id);
        else nextUsersByPod.set(u.pod_id, [u.id]);
      }

      const nextAgentsByUser = new Map<string, string[]>();
      for (const a of (agentsResult.data ?? []) as { id: string; user_id: string }[]) {
        const list = nextAgentsByUser.get(a.user_id);
        if (list) list.push(a.id);
        else nextAgentsByUser.set(a.user_id, [a.id]);
      }

      setPods(visiblePods.sort((a, b) => a.name.localeCompare(b.name)));
      setUsersByPod(nextUsersByPod);
      setAgentsByUser(nextAgentsByUser);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [teamId, canFilterByPod, role, user]);

  // Reset back to "all" if the selected pod disappears from view — e.g. a
  // manager's assignment changed, or the role check just resolved and
  // narrowed the list.
  useEffect(() => {
    if (selectedPodId !== 'all' && pods.length > 0 && !pods.some((p) => p.id === selectedPodId)) {
      setSelectedPodId('all');
    }
  }, [pods, selectedPodId]);

  // Resolved once per pod selection rather than inside each widget, so six
  // cards don't each redo the same users -> agents lookup.
  const selectedPodUserIds = useMemo<string[] | undefined>(() => {
    if (selectedPodId === 'all') return undefined;
    return usersByPod.get(selectedPodId) ?? [];
  }, [selectedPodId, usersByPod]);

  const selectedPodAgentIds = useMemo<string[] | undefined>(() => {
    if (selectedPodId === 'all') return undefined;
    const userIds = usersByPod.get(selectedPodId) ?? [];
    const agentIds: string[] = [];
    for (const userId of userIds) {
      const owned = agentsByUser.get(userId);
      if (owned) agentIds.push(...owned);
    }
    return agentIds;
  }, [selectedPodId, usersByPod, agentsByUser]);

  // Derived, not stored. `selectedDate` is keyed off the END of whatever
  // range is selected and is used for the dashboard's date label.
  //
  // 2026-09-17: TopModelsUsed, TopProjects, NeedsAttentionCard,
  // PeopleAndAgentsPreview, and AgentsCostPanel used to take that same
  // single `selectedDate` and silently ignore the rest of a multi-day
  // selection (see the removed 2026-09-14 note that used to live here) —
  // only TokenSpendOverTime actually read the full range. Those five now
  // take `selectedRange` ({from, to} date-key strings) instead, so picking
  // a week shows that week's totals everywhere, not just on the token-spend
  // chart. Picking "Today"/"Yesterday" still collapses from === to, so
  // nothing changes for the common single-day case.
  const toDateKey = (d: Date) =>
    d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  const rangeStart = dateRange.from ?? dateRange.to ?? new Date();
  const rangeEnd = dateRange.to ?? dateRange.from ?? new Date();
  const selectedDate = toDateKey(rangeEnd);
  const selectedRange = { from: toDateKey(rangeStart), to: toDateKey(rangeEnd) };

  // Current date formatting - fix timezone issue
  const selectedDateObj = new Date(selectedDate + 'T00:00:00');
  const formattedDate = selectedDateObj.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Filter standup data by selected date
  const filterStandupsByDate = (data: any[], date: string) => {
    return data.filter(standup => {
      if (!standup.created_at) return false;
      
      // Handle both date string formats (YYYY-MM-DD and full timestamp)
      let standupDateString;
      if (standup.created_at.includes('T')) {
        // Full timestamp - extract date part
        standupDateString = standup.created_at.split('T')[0];
      } else if (standup.created_at.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // Already in YYYY-MM-DD format
        standupDateString = standup.created_at;
      } else {
        // Parse and reformat other formats
        const standupDate = new Date(standup.created_at + 'T00:00:00'); // Force local parsing
        standupDateString = standupDate.getFullYear() + '-' + 
          String(standupDate.getMonth() + 1).padStart(2, '0') + '-' + 
          String(standupDate.getDate()).padStart(2, '0');
      }
      
      return standupDateString === date;
    });
  };

  const dateFilteredData = filterStandupsByDate(standupData, selectedDate);

  // Get unique dates for date picker options - convert to Date objects
  const availableDates = [...new Set(standupData.map(standup => {
    if (!standup.created_at) return null;
    
    // Handle both date string formats (same logic as filtering)
    let standupDateString;
    if (standup.created_at.includes('T')) {
      // Full timestamp - extract date part
      standupDateString = standup.created_at.split('T')[0];
    } else if (standup.created_at.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // Already in YYYY-MM-DD format
      standupDateString = standup.created_at;
    } else {
      // Parse and reformat other formats
      const standupDate = new Date(standup.created_at + 'T00:00:00'); // Force local parsing
      standupDateString = standupDate.getFullYear() + '-' + 
        String(standupDate.getMonth() + 1).padStart(2, '0') + '-' + 
        String(standupDate.getDate()).padStart(2, '0');
    }
    
    return standupDateString;
  }).filter(Boolean))].sort().reverse().map(dateString => new Date(dateString + 'T00:00:00'));

  // Calculate team mood based on sentiment values
  const calculateTeamMood = () => {

    if (!dateFilteredData || dateFilteredData.length === 0) {
      return {
        averageMood: 0,
        moodEmoji: '😐',
        moodText: 'Neutral',
        moodColor: '#94a3b8',
        description: 'No mood data available'
      };
    }

    // Extract sentiment values (assuming they exist in the standup data)
    const sentimentValues = dateFilteredData
      .map(standup => {
        return standup.sentiment || 0;
      })
      .filter(sentiment => sentiment !== null && sentiment !== undefined);

    if (sentimentValues.length === 0) {
      return {
        averageMood: 0,
        moodEmoji: '😐',
        moodText: 'Neutral',
        moodColor: '#94a3b8',
        description: 'No mood data available'
      };
    }

    const averageMood = sentimentValues.reduce((sum, sentiment) => sum + sentiment, 0) / sentimentValues.length;

    // Convert sentiment to mood display
    if (averageMood > 0.2) {
      return {
        averageMood,
        moodEmoji: '😊',
        moodText: 'Positive',
        moodColor: '#10b981',
        description: 'Team is feeling great!'
      };
    } else if (averageMood < -0.2) {
      return {
        averageMood,
        moodEmoji: '😔',
        moodText: 'Needs Support',
        moodColor: '#ef4444',
        description: 'Team could use some support'
      };
    } else {
      return {
        averageMood,
        moodEmoji: '😐',
        moodText: 'Neutral',
        moodColor: '#94a3b8',
        description: 'Team is steady'
      };
    }
  };

  const teamMood = calculateTeamMood();

  // Calculate mood trend for the last 14 days
  const calculateMoodTrend = () => {
    const today = new Date();
    const trendData = [];
    
    // Go back 14 days
    for (let i = 13; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateString = date.getFullYear() + '-' + 
        String(date.getMonth() + 1).padStart(2, '0') + '-' + 
        String(date.getDate()).padStart(2, '0');
      
      // Get standups for this date
      const dateStandups = standupData.filter(standup => {
        if (!standup.created_at) return false;
        
        let standupDateString;
        if (standup.created_at.includes('T')) {
          standupDateString = standup.created_at.split('T')[0];
        } else if (standup.created_at.match(/^\d{4}-\d{2}-\d{2}$/)) {
          standupDateString = standup.created_at;
        } else {
          const standupDate = new Date(standup.created_at + 'T00:00:00');
          standupDateString = standupDate.getFullYear() + '-' + 
            String(standupDate.getMonth() + 1).padStart(2, '0') + '-' + 
            String(standupDate.getDate()).padStart(2, '0');
        }
        
        return standupDateString === dateString;
      });
      
      // Calculate average sentiment for the day
      const sentimentValues = dateStandups
        .map(standup => standup.sentiment || 0)
        .filter(sentiment => sentiment !== null && sentiment !== undefined);
      
      const avgSentiment = sentimentValues.length > 0 
        ? sentimentValues.reduce((sum, val) => sum + val, 0) / sentimentValues.length 
        : null;
      
      trendData.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        mood: avgSentiment !== null ? parseFloat((avgSentiment * 100).toFixed(1)) : null,
        responses: dateStandups.length
      });
    }
    
    return trendData;
  };

  const moodTrendData = calculateMoodTrend();

  // Calculate completion stats based on filtered data
  const totalExpected = teamMemberCount || 1; // Use actual team member count
  const totalResponses = dateFilteredData.length;
  const completionPercentage = totalExpected > 0 ? Math.round((totalResponses / totalExpected) * 100) : 0;

  return (
    <div className="max-w-[1800px] mx-auto w-full">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground mb-1">{teamName || 'Dashboard'}</h1>
          <p className="text-muted-foreground text-sm">{formattedDate}</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
          <DateRangePicker
            range={dateRange}
            onRangeChange={(next) => {
              if (next?.from) {
                setDateRange({ from: next.from, to: next.to ?? next.from });
              }
            }}
            // Matches TokenSpendOverTime's own MAX_RANGE_DAYS backstop —
            // without this, the calendar would let someone pick a range
            // wider than that chart can actually draw, and it would just
            // silently truncate instead of the picker ever showing the
            // constraint.
            maxRangeDays={MAX_RANGE_DAYS}
            className="w-full sm:w-64 bg-card border border-border"
            placeholder="Select date range"
          />
          
          {/* Admin/manager only — see the pod-filter state comment above
              for why plain members don't get this control at all.
              Labeled "team" in the UI, not "pod" — matches the existing
              convention in TeamCheckins.tsx (its "Team" column + "All team"
              filter are this same pods.name concept under a different
              page). Data/variable names stay `pod*` internally since that's
              the actual schema concept; only the user-facing copy says
              "team". */}
          {canFilterByPod && pods.length > 0 && (
            <Select value={selectedPodId} onValueChange={setSelectedPodId}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="All teams" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All teams</SelectItem>
                {pods.map((pod) => (
                  <SelectItem key={pod.id} value={pod.id}>
                    {pod.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Metrics Section — hidden by request (2026-08-28): completion rate /
          mood trend / mood today were showing empty/zero state for most
          days and read as broken rather than "no data yet". Calculations
          (completionPercentage, moodTrendData, teamMood, totalResponses)
          are left in place below in case this comes back. */}

      {/* KPI widgets — real data where the schema has it (tokens, branches,
          blockers); admin-gated ones (TokenSpendOverTime, AgentsCostPanel)
          render nothing for non-admins. TopModelsUsed ranks by model, not by
          agent/human, so it isn't gated — see that file's comment.
          One seamless bordered grid with hairline dividers between cells
          (matches the Figma dashboard layout), rather than separate floating
          cards with gaps — each widget below renders as a plain div, not a
          Card, so it doesn't double up its own border/shadow/background.
          bg-background (not bg-white/bg-card): --card resolves to pure white
          in light mode ("kept as white for card separation", per index.css),
          which reads as a glaringly white box against the cream --background
          page tone. Figma has this grid blend into the page instead — the
          hairline border + divide-y/divide-x lines below still separate the
          cells, just without the whole block sitting on its own white fill.
          bg-background is theme-aware on its own (cream light / ink dark),
          so no separate dark: override is needed here. */}
      {/* No border-radius, per Figma (2026-09-29) -- was rounded-lg. */}
      {role === 'manager' && <ManagerBudgetStatusCard teamId={teamId} podId={selectedPodId} />}
      <div className="divide-y divide-border overflow-hidden border border-border bg-background shadow-sm">
        {canViewUsageDetails ? (
          <>
            <div className="grid grid-cols-1 divide-y divide-border lg:grid-cols-4 lg:divide-x lg:divide-y-0">
              <div className="lg:col-span-1">
                <TopModelsUsed teamId={teamId} range={selectedRange} agentIds={selectedPodAgentIds} />
              </div>
              <div className="lg:col-span-2">
                <TokenSpendOverTime teamId={teamId} range={dateRange} agentIds={selectedPodAgentIds} />
              </div>
              <div className="lg:col-span-1">
                <TopProjects teamId={teamId} range={selectedRange} agentIds={selectedPodAgentIds} />
              </div>
            </div>

            <div className="grid grid-cols-1 divide-y divide-border lg:grid-cols-4 lg:divide-x lg:divide-y-0">
              <div className="lg:col-span-1">
                <NeedsAttentionCard teamId={teamId} range={selectedRange} userIds={selectedPodUserIds} />
              </div>
              <div className="lg:col-span-2">
                <PeopleAndAgentsPreview teamId={teamId} range={selectedRange} userIds={selectedPodUserIds} />
              </div>
              <div className="lg:col-span-1">
                <AgentsCostPanel teamId={teamId} range={selectedRange} agentIds={selectedPodAgentIds} />
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-1 divide-y divide-border lg:grid-cols-3 lg:divide-x lg:divide-y-0">
              <div className="lg:col-span-1">
                <MemberDashboardCard teamId={teamId} range={selectedRange} />
              </div>
              <div className="lg:col-span-2">
                <NeedsAttentionCard teamId={teamId} range={selectedRange} />
              </div>
            </div>
            <div>
              <PeopleAndAgentsPreview teamId={teamId} range={selectedRange} />
            </div>
          </>
        )}
      </div>

    </div>
  );
};

export default NewDashboard;
